import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { IconikAsset } from "@/components/iconik/types";

export interface CartContextType {
  items: IconikAsset[];
  isInCart: (item: IconikAsset) => boolean;
  addToCart: (items: IconikAsset[]) => void;
  removeFromCart: (idsToRemove: string[]) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  email: string;
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({
  email,
  children,
}) => {
  const [items, setItems] = useState<IconikAsset[]>([]);
  const storageKey = `mamba-cart-${email}`;

  // Load items from localStorage on mount
  useEffect(() => {
    const savedItems = localStorage.getItem(storageKey);
    if (savedItems) {
      setItems(JSON.parse(savedItems));
    }
  }, [storageKey]);

  const isInCart = useCallback(
    (item: IconikAsset) =>
      !!items.find(
        (cartItem: IconikAsset) => cartItem.assetId === item.assetId
      ),

    [items]
  );

  const addToCart = useCallback(
    (newItems: IconikAsset[]) => {
      setItems((currentItems) => {
        const updatedItems = [
          ...currentItems,
          ...newItems.filter((item) => !currentItems.includes(item)),
        ];
        localStorage.setItem(storageKey, JSON.stringify(updatedItems));
        return updatedItems;
      });
    },
    [storageKey]
  );

  const removeFromCart = useCallback(
    (idsToRemove: string[]) => {
      setItems((currentItems) => {
        const updatedItems = currentItems.filter(
          ({ assetId }) => !idsToRemove.includes(assetId)
        );
        localStorage.setItem(storageKey, JSON.stringify(updatedItems));
        return updatedItems;
      });
    },
    [storageKey]
  );

  const clearCart = useCallback(() => {
    localStorage.removeItem(storageKey);
    setItems([]);
  }, [storageKey]);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        isInCart,
        removeFromCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
